import { useCallback, useEffect, useMemo, useState } from 'react'
import { Table, Button, Select, Col, Row, Spin, notification } from 'antd'
import {
  ProcoreProject,
  ProcoreCompany,
  Project,
  ProjectProcoreConfiguration
} from './Procore'
import { ColumnsType } from 'antd/lib/table/interface'
import { useApi } from 'admin/lib/Api'
import './Additional.css'
import styled from 'styled-components'
import { colours } from '@qflow/theme'
import { useParams } from 'react-router-dom'

type EditingState = {
  selectedCompany?: ProcoreCompany
  isLoadingProjects?: boolean
  companyProjects: ProcoreProject[]
  selectedProject?: ProcoreProject
  isSaving?: boolean
}

export function EditProcore({
  projects,
  procoreCompanies
}: {
  projects: Project[]
  procoreCompanies: ProcoreCompany[]
}) {
  const { accountId } = useParams<{
    accountId: string
  }>()

  const [editingQflowProject, setEditingQflowProject] = useState<
    Project | undefined
  >()
  const [editingState, setEditingState] = useState<EditingState>({
    isLoadingProjects: false,
    companyProjects: []
  })

  const api = useApi()

  const displayError = useCallback((errMsg: string, error: any) => {
    console.error(errMsg, error)
    notification.error({
      message: `Error`,
      description: errMsg,
      placement: 'top',
      style: { width: '90%' }
    })
  }, [])
  const displayWarn = useCallback((errMsg: string) => {
    console.warn(errMsg)
    notification.warning({
      message: `Warning`,
      description: errMsg,
      placement: 'top'
    })
  }, [])

  const setProcoreCompany = useCallback(
    (procoreCompany: ProcoreCompany, preselectProcoreProjectId?: string) => {
      setEditingState({
        selectedCompany: procoreCompany,
        isLoadingProjects: true,
        companyProjects: []
      })

      const loadProcoreProjects = async (companyId: string) => {
        if (!api) return

        // TODO: Cache this data
        const [data, status, error] = await api.getProcoreProjects(companyId)
        if (error) {
          displayError(
            `Failed to retrieve Procore projects for '${companyId}' [${status}] ${error.toString()}`,
            error
          )
          setEditingState(prev => ({
            ...prev,
            isLoadingProjects: false,
            companyProjects: []
          }))
          return
        }

        const procoreProjects = data as ProcoreProject[]
        const preselectedProject = preselectProcoreProjectId
          ? procoreProjects.find(p => p.projectId === preselectProcoreProjectId)
          : undefined

        if (preselectProcoreProjectId && !preselectedProject) {
          displayWarn(
            `Unable to find Procore Project '${preselectProcoreProjectId}' in the list of all Procore projects.`
          )
        }

        setEditingState(prev => ({
          ...prev,
          isLoadingProjects: false,
          companyProjects: procoreProjects,
          selectedProject: preselectedProject ?? prev.selectedProject
        }))
      }

      void loadProcoreProjects(procoreCompany.companyId)
    },
    [api, displayError, displayWarn]
  )

  const clearProcoreCompany = useCallback(() => {
    setEditingState({
      companyProjects: []
    })
  }, [])

  const setProcoreProject = useCallback((project?: ProcoreProject) => {
    setEditingState(prev => ({
      ...prev,
      selectedProject: project
    }))
  }, [])

  const setEditing = useCallback(
    (qflowProject?: Project) => {
      clearProcoreCompany()
      setEditingQflowProject(qflowProject)
      if (qflowProject) {
        if (qflowProject.procoreConfiguration) {
          const procoreCompany = procoreCompanies.find(
            c => c.companyId === qflowProject.procoreConfiguration!.companyId
          )
          if (!procoreCompany) {
            displayWarn(
              `Project '${qflowProject.name}' has Procore Company '${qflowProject.procoreConfiguration!.companyName}' [${qflowProject.procoreConfiguration!.companyId}] which was not found in the list of all Procore companies.`
            )
            return
          }
          setProcoreCompany(
            procoreCompany!,
            qflowProject.procoreConfiguration.projectId
          )
        }
      }
    },
    [clearProcoreCompany, displayWarn, procoreCompanies, setProcoreCompany]
  )

  const setSaving = useCallback(() => {
    setEditingState(prev => ({
      ...prev,
      isSaving: true
    }))
  }, [])

  useEffect(() => {
    if (!editingState.isSaving || !api) return

    async function SaveChanges() {
      if (editingState.selectedCompany && !editingState.selectedProject) {
        throw 'Cannot Save with Company selected, but no Project selected'
      }

      const projectProcoreConfiguration =
        !editingState.selectedCompany && !editingState.selectedProject
          ? null
          : ({
            companyId: editingState.selectedCompany!.companyId,
            companyName: editingState.selectedCompany!.name,
            projectId: editingState.selectedProject!.projectId,
            projectName: editingState.selectedProject!.name,
            projectNumber: editingState.selectedProject!.projectNumber
          } as ProjectProcoreConfiguration)

      const [, status, error] =
        !editingState.selectedCompany && !editingState.selectedProject
          ? await api!.deleteProjectProcore(
            accountId ? accountId : '',
            editingQflowProject!.projectId
          )
          : await api!.updateProjectProcore(
            accountId ? accountId : '',
            editingQflowProject!.projectId,
            {
              companyId: projectProcoreConfiguration!.companyId,
              projectId: projectProcoreConfiguration!.projectId
            }
          )

      if (error) {
        displayError(
          `Failed to save Procore changes to project '${editingQflowProject?.name}'. [${status}] ${error.toString()}`,
          error
        )
        setEditingState(prev => ({
          ...prev,
          isSaving: false
        }))
        return
      }

      // Apply successful changes to local instance
      editingQflowProject!.procoreConfiguration = projectProcoreConfiguration

      notification.success({
        message: `Procore details successfully saved.`,
        description: `Procore details were successfully saved for project '${editingQflowProject?.name}'`,
        placement: 'top'
      })
      setEditing()
    }

    void SaveChanges()
  }, [
    accountId,
    api,
    displayError,
    editingQflowProject,
    editingState.isSaving,
    editingState.selectedCompany,
    editingState.selectedProject,
    setEditing
  ])

  const renderQflowProjectColumn = useCallback(
    (qflowProject: Project) => {
      if (
        editingQflowProject &&
        editingQflowProject.projectId !== qflowProject.projectId
      ) {
        return (
          <span style={{ fontWeight: 700, opacity: '0.3' }}>
            {qflowProject.name}
          </span>
        )
      }
      return <span style={{ fontWeight: 700 }}>{qflowProject.name}</span>
    },
    [editingQflowProject]
  )

  const renderCompanyColumn = useCallback(
    (qflowProject: Project) => {
      if (editingQflowProject === qflowProject) {
        return (
          <>
            <Row>
              <Select
                id="procoreCompanySelect"
                allowClear
                showSearch
                optionFilterProp="label"
                optionLabelProp="label"
                style={{ width: '100%' }}
                placeholder="Select Procore Company"
                aria-label="Select Procore Company"
                defaultValue={
                  editingQflowProject.procoreConfiguration?.companyId
                }
                disabled={editingState.isSaving}
                onChange={companyId => {
                  if (companyId) {
                    setProcoreCompany(
                      procoreCompanies.find(c => c.companyId === companyId)!
                    )
                  }
                }}
                onClear={() => clearProcoreCompany()}
              >
                {procoreCompanies.map(({ companyId, name, logoUrl }) => (
                  <Select.Option
                    role="option"
                    key={companyId}
                    value={companyId}
                    label={name}
                    aria-label={name}
                  >
                    <CompanySelectOption companyName={name} logoUrl={logoUrl} />
                  </Select.Option>
                ))}
              </Select>
            </Row>
            <Row>
              <CompanyDetailsBox
                procoreCompany={editingState?.selectedCompany}
              />
            </Row>
          </>
        )
      } else {
        return qflowProject.procoreConfiguration?.companyName
      }
    },
    [
      clearProcoreCompany,
      editingQflowProject,
      editingState.isSaving,
      editingState?.selectedCompany,
      procoreCompanies,
      setProcoreCompany
    ]
  )

  const renderEditableProcoreProjectRowLabel = (procoreProjectName: string, procoreProjectNumber: string | null, isDemo: boolean) => {
    const projectLabel = procoreProjectNumber
      ? `${procoreProjectNumber} - ${procoreProjectName}`
      : procoreProjectName;
    return isDemo
      ? `${projectLabel} [TEST PROJECT]`
      : projectLabel;
  };

  const renderAssignedProcoreProjectRowLabel = (procoreProjectName: string | undefined, procoreProjectNumber: string | null | undefined) => {
    return procoreProjectNumber
      ? `${procoreProjectNumber} - ${procoreProjectName}`
      : procoreProjectName;
  };

  const renderProjectColumn = useCallback(
    (qflowProject: Project) => {
      if (editingQflowProject === qflowProject) {
        const sortedProjectsWithLabels = editingState?.companyProjects.map(
          p => ({
            ...p,
            label: renderEditableProcoreProjectRowLabel(p.name, p.projectNumber, p.isDemo)
          })
        )?.sort((a, b) => (a.label > b.label) ? 1 : ((b.label > a.label) ? -1 : 0)) ?? [];
        return (
          <>
            <Row>
              <Select
                id="procoreProjectSelect"
                allowClear
                showSearch
                optionFilterProp="label"
                style={{ width: '100%' }}
                placeholder="Select Procore Project"
                aria-label="Select Procore Project"
                defaultValue={
                  editingQflowProject.procoreConfiguration?.companyId ==
                    editingState.selectedCompany?.companyId
                    ? editingQflowProject.procoreConfiguration?.projectId
                    : null
                }
                disabled={
                  !editingState?.selectedCompany ||
                  editingState?.isLoadingProjects ||
                  editingState.isSaving
                }
                loading={editingState?.isLoadingProjects}
                onChange={projectId => {
                  if (projectId) {
                    setProcoreProject(
                      editingState.companyProjects.find(
                        p => p.projectId === projectId
                      )!
                    )
                  }
                }}
                onClear={() => setProcoreProject()}
              >
                {sortedProjectsWithLabels.map(
                  ({ projectId, label }) => (
                    <Select.Option
                      role="option"
                      key={projectId}
                      value={projectId}
                      label={label}
                      aria-label={label}
                    >
                      {label}
                    </Select.Option>
                  )
                )}
              </Select>
            </Row>
            <Row>
              <ProjectDetailsBox
                procoreProject={editingState?.selectedProject}
              />
            </Row>
          </>
        )
      } else {
        return renderAssignedProcoreProjectRowLabel(qflowProject.procoreConfiguration?.projectName, qflowProject.procoreConfiguration?.projectNumber)
      }
    },
    [editingQflowProject, editingState, setProcoreProject]
  )

  const renderEditColumn = useCallback(
    (qflowProject: Project) => {
      return editingQflowProject !== qflowProject ? (
        <Button
          onClick={() => setEditing(qflowProject)}
          disabled={!!editingQflowProject}
        >
          Edit
        </Button>
      ) : (
        <Col>
          <Button
            style={{ marginRight: '10px' }}
            onClick={() => setEditing()}
            disabled={editingState.isSaving}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            disabled={
              editingState.isSaving ||
              (editingState.selectedCompany && !editingState.selectedProject)
            }
            onClick={setSaving}
            aria-label="Save"
            style={{ width: '60px' }}
          >
            <SaveButtonText isSaving={editingState.isSaving} />
          </Button>
        </Col>
      )
    },
    [
      editingQflowProject,
      editingState.isSaving,
      editingState.selectedCompany,
      editingState.selectedProject,
      setEditing,
      setSaving
    ]
  )

  const columns: ColumnsType<Project> = [
    {
      title: 'Project Name',
      render: (_, qflowProject, __) => renderQflowProjectColumn(qflowProject),
      sorter: (a, b) => a.name.localeCompare(b.name)
    },
    {
      title: 'Procore Company',
      render: (_, qflowProject, __) => renderCompanyColumn(qflowProject),
      width: '30%'
    },
    {
      title: 'Procore Project',
      render: (_, qflowProject, __) => renderProjectColumn(qflowProject),
      width: '30%'
    },
    {
      title: 'Edit',
      key: 'edit',
      width: '11rem',
      render: (_, qflowProject, __) => renderEditColumn(qflowProject),
      align: 'right'
    }
  ]

  return (
    <Table
      columns={columns}
      dataSource={projects}
      rowKey={row => row.projectId}
      pagination={false}
      style={{ width: '100%', margin: '3rem 1rem' }}
      rowClassName={editingQflowProject ? 'top-align editing' : 'top-align'}
    />
  )
}

function SaveButtonText({ isSaving }: { isSaving?: boolean }) {
  if (!isSaving) return <>Save</>

  return <Spin size="small" />
}

function CompanySelectOption({
  companyName,
  logoUrl
}: {
  companyName: string
  logoUrl: string | null
}) {
  return (
    <Row align="middle">
      <LogoDiv className="selectCompanyLogo">
        {logoUrl && <LogoImg src={logoUrl} alt={`Logo of ${companyName}`} />}
        {!logoUrl && <LogoPlaceholderDiv>No Logo</LogoPlaceholderDiv>}
      </LogoDiv>
      {companyName}
    </Row>
  )
}

const LogoImg = styled.img`
  max-width: 100%;
  max-height: 60px;
`

const LogoDiv = styled.div`
  width: 60px;
  max-width: 60px;
  margin-right: 10px;
`

const LogoPlaceholderDiv = styled.div`
  height: 30px;

  border: 1px solid ${colours.BORDER_GREY};
  color: ${colours.BORDER_GREY};
  line-height: 28px;
  text-align: center;
  font-size: 10px;
`

function CompanyDetailsBox({
  procoreCompany
}: {
  procoreCompany?: ProcoreCompany | null
}) {
  const logo = useMemo(() => {
    if (procoreCompany?.logoUrl) {
      return (
        <CompanyDetailsLogoImg
          src={procoreCompany.logoUrl}
          alt="Company Logo"
        />
      )
    }

    return <span>No Logo</span>
  }, [procoreCompany])

  if (!procoreCompany) {
    return <CompanyDetailsBoxDiv></CompanyDetailsBoxDiv>
  }

  return <CompanyDetailsBoxDivActive>{logo}</CompanyDetailsBoxDivActive>
}

const CompanyDetailsBoxDiv = styled.div`
  width: 100%;
  min-height: 100px;
  border-radius: 5px;
  margin-top: 15px;

  display: flex;
  justify-content: center;
  align-items: center;
`
const CompanyDetailsBoxDivActive = styled(CompanyDetailsBoxDiv).attrs({
  className: 'procore-company-details-active'
})`
  border: 1px solid ${colours.CLOUD_GREY};
`
const CompanyDetailsLogoImg = styled.img`
  max-width: 85%;
  max-height: 50px;
`

function ProjectDetailsBox({
  procoreProject
}: {
  procoreProject?: ProcoreProject | null
}) {
  const detailsRow = useCallback(
    (title: string, value: string) => (
      <Row>
        <ProjectDetailsRowLabel>{title}: </ProjectDetailsRowLabel>
        {value}
      </Row>
    ),
    []
  )

  const formatAddress = useCallback((project: ProcoreProject) => {
    return [
      project.streetAddress,
      project.city,
      project.county,
      project.zipCode
    ]
      .filter(x => typeof x === 'string' && x.length > 0)
      .join(', ')
  }, [])

  if (!procoreProject) {
    return <ProjectDetailsBoxDiv />
  }

  return (
    <ProjectDetailsBoxDivActive>
      {detailsRow('Address', formatAddress(procoreProject))}
      {detailsRow('Country', procoreProject.countryCode ?? '')}
      {detailsRow('Procore Project ID', procoreProject.projectId)}
      {detailsRow('Start date', procoreProject.startDate)}
      {detailsRow('Completion date', procoreProject.completionDate)}
    </ProjectDetailsBoxDivActive>
  )
}

const ProjectDetailsBoxDiv = styled.div`
  width: 100%;
  min-height: 100px;
  border-radius: 5px;
  margin-top: 15px;

  padding: 12px 13px 8px 13px;
  font-size: 11px;
  line-height: 16px;
  color: ${colours.TEAL};
`
const ProjectDetailsBoxDivActive = styled(ProjectDetailsBoxDiv).attrs({
  className: 'procore-project-details-active'
})`
  background-color: ${colours.PALE_TEAL};
`

const ProjectDetailsRowLabel = styled.span`
  margin-right: 3px;
  font-weight: 700;
`
